import { createActions } from 'reduxsauce';

export interface IRecipientState {
  data: IRecipientData | null;
  nonProfitData: IRecipientData | null;
  isLoading: boolean;
  error: string | null;
}
export interface IRecipientData {
  recipients: IRecipients[];
}
export type Competitor = {
  // not yet in response, needed for hvp
  competitor_name: string;
  raised: string;
  coh: string;
  is_incumbent: boolean;
};

export type MiscIds = {
  photo_id?: string;
  ext_de_url_id?: string;
};

export type CampaignDetails = {
  campaign_website?: string;
};

export type Election = {
  display_info?: string;
  election_goal: string;
  state: string;
  projected_cost: string;
  contribution_limit: string;
  is_employer_address_required: boolean;
  ratings: Array<string | null>;
  // pvi not in response, needed for hvp
  competitor?: Competitor; // not yet in response, needed for hvp
  primary_election_date?: string; // not yet in response, needed for hvp
};

export type Race = {
  is_incumbent: boolean;
  is_self_funded: boolean;
  is_pay_to_play_issue: boolean;
  amount_raised: string;
  coh?: string; // not yet in response, needed for hvp
  financials_updated_at?: string; // not yet in response, needed for hvp
  campaign_details: CampaignDetails;
  election: Election;
};
export interface IRecipients {
  recipient_id: string;
  de_id: string;
  stripe_id?: string;
  misc_ids?: MiscIds;
  recipient_type: string;
  recipient_subtype: string;
  de_status: string;
  stripe_status: string;
  impact_score: string;
  recipient_name?: string;
  race?: Race;
  is_stripe_authorized?: boolean;
  onboarding_form_complete?: boolean;
}

export type AdminRecipientListElection = {
  display_info: string | null;
  election_date: string | null;
};

export type AdminRecipientListRace = {
  election: AdminRecipientListElection;
};

// GET /admin/recipients returns the backend's trimmed AdminRecipientListItem — no misc_ids, and a
// race tree cut to the two election fields the list renders. IRecipients describes
// /admin/recipients/search, which still returns the full race tree.
export type AdminRecipientListItem = {
  recipient_id: string;
  recipient_name: string;
  de_id: string | null;
  stripe_id: string | null;
  recipient_type: string;
  recipient_subtype: string | null;
  de_status: string;
  stripe_status: string;
  recipient_status: string;
  impact_score: string | null;
  is_stripe_authorized: boolean;
  election_level: string | null;
  onboarding_form_complete: boolean;
  race: AdminRecipientListRace | null;
};

export enum RecipientsRequestType {
  donate_page = 'DONATE_PAGE',
  donor_search = 'DONOR_SEARCH',
  admin = 'ADMIN',
}

export type RecipientProps = {
  search_str?: string;
  recipient_ids?: string[];
  payment_processor?: string;
  is_public_searchable?: boolean;
  request_type?: RecipientsRequestType;
};

export type NonProfitProps = {
  display_name: string;
  legal_name: string;
  type: string;
  subtype: string;
  photo_id: string;
  de_api_id?: string;
  de_url_id: string;
  is_public_searchable: boolean;
  legal_id?: string;
  primary_contact_name?: string;
  primary_contact_email?: string;
  recipient_status?: string;
  is_stripe_authorized?: boolean;
  onboarding_form_complete?: boolean;
  stripe_status?: string;
  stripe_id?: string;
  de_status?: string;
  public_fundraising_query_str?: string;
};

export type LookupRecipient = {
  recipient_id: string;
  display_name: string;
  legal_id: string | null;
  subtype: string | null;
};

export interface IAdminRecipientDetail {
  recipient_id: string;
  entity_id: string;
  display_name: string;
  legal_name?: string;
  legal_id?: string;
  type: string;
  subtype?: string;
  primary_contact_email?: string;
  primary_contact_name?: string;
  misc_ids?: { photo_id?: string; ext_de_url_id?: string; [key: string]: string | undefined };
  recipient_status?: string;
  is_stripe_authorized: boolean;
  onboarding_form_complete: boolean;
  stripe_id?: string;
  de_status?: string;
  is_public_searchable: boolean;
  external_disbursement_method?: string | null;
  de_id?: string;
  public_fundraising_query_str?: string | null;
}

export type DuplicateType = 'display_name' | 'ein';

export type DuplicateConflictPayload = {
  duplicate_type: DuplicateType;
  recipients: LookupRecipient[];
  debugging_id?: string;
};

export type PatchRecipientPayload = {
  display_name?: string | null;
  legal_name?: string | null;
  subtype?: string | null;
  photo_id?: string | null;
  de_url_id?: string | null;
  is_public_searchable?: boolean | null;
  legal_id?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  recipient_status?: string | null;
  is_stripe_authorized?: boolean | null;
  onboarding_form_complete?: boolean | null;
  public_fundraising_query_str?: string | null;
};

export type AccountsLinkProps = {
  recipient_id: string;
  type: 'account_onboarding' | 'account_update';
};

export interface RecipientTypes {
  RECIPIENTS: 'RECIPIENTS';
  RECIPIENTS_START: 'RECIPIENTS_START';
  RECIPIENTS_SUCCESS: 'RECIPIENTS_SUCCESS';
  RECIPIENTS_ERROR: 'RECIPIENTS_ERROR';
  RECIPIENTS_CLEAN_UP: 'RECIPIENTS_CLEAN_UP';
  NON_PROFIT_SUCCESS: 'NON_PROFIT_SUCCESS';
  NON_PROFIT_ERROR: 'NON_PROFIT_ERROR';
}

const { Types, Creators } = createActions<RecipientTypes>({
  recipients: ['data'],
  recipientsStart: null,
  recipientsSuccess: ['data'],
  recipientsError: ['error'],
  recipientsCleanUp: null,
  nonProfitSuccess: ['data'],
  nonProfitError: ['error'],
});

export { Types };

export default Creators;
